import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { COMPANY } from "@/data/mockData";
import { Instagram, Facebook, Youtube, ArrowRight, Music2 } from "lucide-react";
import { toast } from "sonner";
import { fetchCategories } from "@/services/api";

// Shown until the live categories arrive (or if Firestore is unreachable).
const SHOP_FALLBACK = [
  { key: "books", to: "/shop/books", label: "Shop Books" },
  { key: "collectibles", to: "/shop/collectibles", label: "Shop Collectibles" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [shopLinks, setShopLinks] = useState(SHOP_FALLBACK);

  // Mirrors whatever categories exist in the admin console.
  useEffect(() => {
    fetchCategories()
      .then((cats) => {
        if (cats.length) {
          setShopLinks(
            cats.map((c) => ({ key: c.slug, to: `/shop/${c.slug}`, label: `Shop ${c.name}` }))
          );
        }
      })
      .catch(() => {});
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    // ⚠️ BACKEND INTEGRATION POINT: send to /api/newsletter
    toast.success("Subscribed", {
      description: "We'll send heritage stories to your inbox.",
    });
    setEmail("");
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#281a12] text-[#FDFBF7] mt-24 grain overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-12 relative z-10">
        <div className="md:col-span-5">
          <div className="font-serif-display text-3xl md:text-4xl leading-tight">
            Heritage, told with intention.
          </div>
          <p className="mt-4 text-[#D1C3B4] text-sm leading-relaxed max-w-md">
            {COMPANY.name}, a Singapore-based research and publishing house,
            captures South-east Asian history through books, collectibles and
            multimedia storytelling.
          </p>

          <form
            data-testid="footer-newsletter-form"
            onSubmit={handleSubscribe}
            className="mt-8 flex items-center border-b border-[#D1C3B4]/30 focus-within:border-[#FDFBF7] max-w-md"
          >
            <input
              data-testid="footer-newsletter-input"
              type="email"
              required
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent text-[#FDFBF7] placeholder-[#D1C3B4]/60 py-3 outline-none text-sm"
            />
            <button
              data-testid="footer-newsletter-submit"
              type="submit"
              className="p-3 text-[#FDFBF7] hover:text-[#946f36] transition-colors"
              aria-label="Subscribe"
            >
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </form>
        </div>

        <div className="md:col-span-3">
          <div className="overline text-[#946f36] mb-5">Explore</div>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/about" className="hover:text-[#946f36]" data-testid="footer-link-about">About</Link></li>
            <li><Link to="/founder" className="hover:text-[#946f36]" data-testid="footer-link-founder">Founder</Link></li>
            <li><Link to="/featured-book" className="hover:text-[#946f36]" data-testid="footer-link-featured-book">Featured Book</Link></li>
            {shopLinks.map((c) => (
              <li key={c.key}><Link to={c.to} className="hover:text-[#946f36]" data-testid={`footer-link-shop-${c.key}`}>{c.label}</Link></li>
            ))}
            <li><Link to="/multimedia" className="hover:text-[#946f36]" data-testid="footer-link-multimedia">Multimedia</Link></li>
            <li><Link to="/contact" className="hover:text-[#946f36]" data-testid="footer-link-contact">Contact</Link></li>
            <li><Link to="/track" className="hover:text-[#946f36]" data-testid="footer-link-track">Track Order</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="overline text-[#946f36] mb-5">Contact</div>
          <ul className="space-y-2 text-sm text-[#D1C3B4]">
            <li>{COMPANY.name}</li>
            <li>UEN: {COMPANY.uen}</li>
            <li>
              <a className="hover:text-[#FDFBF7]" href={`mailto:${COMPANY.email}`} data-testid="footer-email">
                {COMPANY.email}
              </a>
            </li>
            <li>
              <a className="hover:text-[#FDFBF7]" href={`tel:${COMPANY.phone.replace(/\s+/g, "")}`} data-testid="footer-phone">
                {COMPANY.phone}
              </a>
            </li>
            <li>
              <a
                href={COMPANY.whatsapp}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="footer-whatsapp-link"
                className="inline-block mt-3 border border-[#946f36] text-[#946f36] px-5 py-2.5 text-xs uppercase tracking-[0.18em] hover:bg-[#946f36] hover:text-[#281a12] transition-colors"
              >
                WhatsApp Us
              </a>
            </li>
          </ul>

          <div className="mt-8 flex items-center gap-4">
            <a href={COMPANY.socials.instagram} target="_blank" rel="noreferrer noopener" data-testid="footer-social-instagram" aria-label="Instagram" className="text-[#D1C3B4] hover:text-[#FDFBF7]">
              <Instagram className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a href={COMPANY.socials.facebook} target="_blank" rel="noreferrer noopener" data-testid="footer-social-facebook" aria-label="Facebook" className="text-[#D1C3B4] hover:text-[#FDFBF7]">
              <Facebook className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a href={COMPANY.socials.youtube} target="_blank" rel="noreferrer noopener" data-testid="footer-social-youtube" aria-label="YouTube" className="text-[#D1C3B4] hover:text-[#FDFBF7]">
              <Youtube className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a href={COMPANY.socials.tiktok} target="_blank" rel="noreferrer noopener" data-testid="footer-social-tiktok" aria-label="TikTok" className="text-[#D1C3B4] hover:text-[#FDFBF7]">
              <Music2 className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-[#FDFBF7]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#D1C3B4]">
          <div className="flex flex-col gap-1 items-center md:items-start">
            <div>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</div>
            <div>
              Designed by{" "}
              <a
                href="https://www.DigitalGuru.app"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="footer-designed-by"
                className="hover:text-[#FDFBF7] underline underline-offset-2"
              >
                www.DigitalGuru.app
              </a>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="hover:text-[#FDFBF7]" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#FDFBF7]" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
